import { useRef } from "react";
import { Box, Link as MuiLink } from "@mui/material";
import { AttachFile as AttachFileIcon } from "@mui/icons-material";
import { generateDownloadUrl } from "@/services/taskerAttachments";
import { getDocumentDownloadUrl } from "@/services/vendorProfileService";
import { readErrorMessage } from "@/pages/VendorOnboarding/utils/mapBeErrors";
import { isSafeHttpUrl } from "../utils/safeUrl";

export type DocValue = {
  file_name: string;
  download_url?: string;
  document_id?: number;
  attachment_id?: string;
};

export function parseDocumentValue(value: unknown): DocValue | null {
  if (value == null || value === "") return null;
  if (typeof value === "string") return { file_name: value };
  if (typeof value !== "object" || Array.isArray(value)) return null;
  const obj = value as {
    file_name?: unknown;
    download_url?: unknown;
    document_id?: unknown;
    attachment_id?: unknown;
  };
  const hasIdentifier =
    typeof obj.file_name === "string" ||
    typeof obj.download_url === "string" ||
    typeof obj.document_id === "number" ||
    typeof obj.attachment_id === "string";
  if (!hasIdentifier) return null;
  return {
    file_name:
      typeof obj.file_name === "string" && obj.file_name
        ? obj.file_name
        : "attachment",
    download_url:
      typeof obj.download_url === "string" ? obj.download_url : undefined,
    document_id:
      typeof obj.document_id === "number" ? obj.document_id : undefined,
    attachment_id:
      typeof obj.attachment_id === "string" ? obj.attachment_id : undefined,
  };
}

type Props = {
  doc: DocValue | null;
  strikethrough?: boolean;
  onError?: (message: string) => void;
};

export function DocumentDiffCell({ doc, strikethrough, onError }: Props) {
  const inflight = useRef(false);
  if (!doc) {
    return (
      <Box
        component="span"
        sx={{
          textDecoration: strikethrough ? "line-through" : undefined,
          color: strikethrough ? "text.secondary" : undefined,
          fontWeight: strikethrough ? undefined : 500,
        }}
      >
        —
      </Box>
    );
  }
  const label = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        textDecoration: strikethrough ? "line-through" : undefined,
        color: strikethrough ? "text.secondary" : undefined,
        fontWeight: strikethrough ? undefined : 500,
      }}
    >
      <AttachFileIcon sx={{ fontSize: 14 }} />
      {doc.file_name}
    </Box>
  );
  if (doc.download_url && isSafeHttpUrl(doc.download_url)) {
    return (
      <MuiLink
        href={doc.download_url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        {label}
      </MuiLink>
    );
  }
  const openViaFetch = async (
    e: React.MouseEvent,
    fetchUrl: () => Promise<string>,
  ) => {
    e.preventDefault();
    if (inflight.current) return;
    inflight.current = true;
    try {
      const url = await fetchUrl();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      onError?.(readErrorMessage(err, "Could not open attachment preview."));
    } finally {
      inflight.current = false;
    }
  };
  const fetchUrl = doc.document_id !== undefined
    ? () => getDocumentDownloadUrl(doc.document_id!, "bouncer")
    : doc.attachment_id
      ? () => generateDownloadUrl(doc.attachment_id!, "reviewer")
      : null;
  if (!fetchUrl) return label;
  return (
    <MuiLink
      component="button"
      type="button"
      onClick={(e: React.MouseEvent) => openViaFetch(e, fetchUrl)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        bgcolor: "transparent",
        border: "none",
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
      }}
    >
      {label}
    </MuiLink>
  );
}

export default DocumentDiffCell;
